/**
 * Lets a reload (an app update, a stale chunk) put the screen back where it was.
 *
 * Just before reloading, `reloadPage()` stores the view — whatever the registered providers report (active tab,
 * split-pane layout) plus the scroll position of every scrolled element — in sessionStorage. The next load reads it
 * once (`takeReloadState`): components start from it and `restoreScroll` re-applies the scrolling as the page
 * fills in. Grid zoom/text size already live in localStorage (prefStorage), so they come back on their own.
 * Anything older than a minute is ignored, so an ordinary visit never picks up a stale view.
 */

const KEY = 'reloadViewState';
const MAX_AGE_MS = 60_000;

interface ScrollEntry { path: string; top: number; left: number }
interface Saved {
  at: number;
  url: string;
  view: Record<string, unknown>;
  scroll: ScrollEntry[];
}

const providers = new Map<string, () => unknown>();

/** Report one piece of view state for the reload snapshot. Returns the unregister function. */
export function registerViewState(name: string, read: () => unknown): () => void {
  providers.set(name, read);
  return () => { if (providers.get(name) === read) providers.delete(name); };
}

/** A selector for the element from the root by child positions — stable while the same screen is on show. */
function pathOf(el: Element): string {
  const parts: string[] = [];
  for (let node: Element | null = el; node && node !== document.documentElement; node = node.parentElement) {
    const parent: Element | null = node.parentElement;
    parts.unshift(parent ? String(Array.prototype.indexOf.call(parent.children, node)) : '0');
  }
  return parts.join('/');
}

function byPath(path: string): Element | null {
  let node: Element | null = document.documentElement;
  for (const part of path.split('/')) {
    node = node?.children[Number(part)] ?? null;
    if (!node) return null;
  }
  return node;
}

function scrolledElements(): ScrollEntry[] {
  const out: ScrollEntry[] = [];
  const root = document.scrollingElement;
  if (root && (root.scrollTop || root.scrollLeft)) out.push({ path: '', top: root.scrollTop, left: root.scrollLeft });
  for (const el of Array.from(document.body.querySelectorAll('*'))) {
    if (el.scrollTop || el.scrollLeft) out.push({ path: pathOf(el), top: el.scrollTop, left: el.scrollLeft });
  }
  return out;
}

export function saveReloadState(): void {
  try {
    const view: Record<string, unknown> = {};
    providers.forEach((read, name) => { try { view[name] = read(); } catch { /* skip this one */ } });
    const saved: Saved = { at: Date.now(), url: location.pathname + location.search, view, scroll: scrolledElements() };
    sessionStorage.setItem(KEY, JSON.stringify(saved));
  } catch { /* storage blocked: the reload just starts fresh */ }
}

/** Save the view, then reload. Use this instead of location.reload() wherever the app reloads itself. */
export function reloadPage(): void {
  saveReloadState();
  window.location.reload();
}

// Read once, at module load: the first component to ask and the scroll restore see the same snapshot.
let taken: Saved | null = (() => {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Saved;
    const fresh = Date.now() - saved.at < MAX_AGE_MS && saved.url === location.pathname + location.search;
    return fresh ? saved : null;
  } catch { return null; }
})();

/** What a component saved before the reload under `name`, or undefined (also for any normal page load). */
export function reloadedView<T>(name: string): T | undefined {
  return taken?.view[name] as T | undefined;
}

/**
 * Put the scrolling back. The screen is empty at first (the roster loads after the page), so each element is
 * retried until it exists and is tall enough, for a few seconds — and dropped the moment the user scrolls
 * themselves, so the restore never fights them.
 */
export function restoreScroll(): void {
  const saved = taken;
  taken = null;
  if (!saved || saved.scroll.length === 0) return;
  let pending = saved.scroll.slice();
  const stop = () => { pending = []; };
  const opts = { once: true, passive: true, capture: true } as const;
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((e) => window.addEventListener(e, stop, opts));
  const startedAt = Date.now();
  const tick = () => {
    pending = pending.filter((entry) => {
      const el = entry.path === '' ? document.scrollingElement : byPath(entry.path);
      if (!el || el.scrollHeight - el.clientHeight < entry.top) return true;   // not on screen / loaded yet
      el.scrollTop = entry.top;
      el.scrollLeft = entry.left;
      return false;
    });
    if (pending.length === 0 || Date.now() - startedAt > 6000) {
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((e) => window.removeEventListener(e, stop, opts));
      return;
    }
    window.setTimeout(tick, 150);
  };
  tick();
}
